import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { useAccionFactura, useAcreditadoDeFactura, useDescargarPdfFactura } from '@/hooks/useFacturas'
import type { FacturaResumen } from '@/services/facturasService'
import { ConfirmarAnulacionDialog, type AccionDeFactura } from './FacturaDeCobro'
import { NotaCreditoParcialDialog } from './NotaCreditoParcialDialog'

/**
 * Acciones del menú de una factura, compartidas por Movimientos y la página de Facturas. Anular pasa por
 * una confirmación y la nota de crédito parcial por su formulario: `dialogos` se renderiza una vez junto a
 * la tabla.
 */
export function useAccionesDeFactura() {
  const accionFactura = useAccionFactura()
  const descargarFactura = useDescargarPdfFactura()
  // `mutate` es estable; el objeto de la mutación cambia en cada render y recalcularía las columnas de la tabla.
  const { mutate: mutarFactura, mutateAsync: mutarFacturaAsync } = accionFactura
  const { mutate: descargar } = descargarFactura
  const [aAnular, setAAnular] = useState<FacturaResumen | null>(null)
  const [aAcreditar, setAAcreditar] = useState<FacturaResumen | null>(null)
  const acreditadoDeAAnular = useAcreditadoDeFactura(aAnular?.id)

  const onAccion = useCallback(
    (accion: AccionDeFactura, factura: FacturaResumen) => {
      if (accion === 'pdf') {
        descargar(factura.id)
        return
      }
      if (accion === 'anular') {
        setAAnular(factura)
        return
      }
      if (accion === 'acreditar') {
        setAAcreditar(factura)
        return
      }
      mutarFactura(
        { tipo: accion, facturaId: factura.id },
        {
          onSuccess: () => {
            if (accion === 'enviarEmail') toast.success('Enviada por mail')
            if (accion === 'reintentar') toast.success('Factura pedida de nuevo')
          },
        },
      )
    },
    [mutarFactura, descargar],
  )

  const dialogos = (
    <>
      <ConfirmarAnulacionDialog
        // Remonta con cada factura: el check del mail arranca prendido y no arrastra la elección anterior.
        key={aAnular?.id ?? 'ninguna'}
        factura={aAnular}
        acreditado={acreditadoDeAAnular.data}
        onOpenChange={(abierto) => !abierto && setAAnular(null)}
        onConfirmar={(enviarEmail) => {
          if (!aAnular) return
          mutarFactura(
            { tipo: 'anular', facturaId: aAnular.id, enviarEmail },
            { onSuccess: () => toast.success('Nota de crédito pedida: se emite en unos segundos') },
          )
          setAAnular(null)
        }}
      />
      <NotaCreditoParcialDialog
        // Remonta con cada factura: el formulario arranca vacío y relee lo acreditado.
        key={`nc-${aAcreditar?.id ?? 'ninguna'}`}
        factura={aAcreditar}
        onOpenChange={(abierto) => !abierto && setAAcreditar(null)}
        onEmitir={async (nota) => {
          if (!aAcreditar) return
          await mutarFacturaAsync({ tipo: 'acreditar', facturaId: aAcreditar.id, ...nota })
          toast.success('Nota de crédito pedida: se emite en unos segundos')
        }}
      />
    </>
  )

  return { onAccion, dialogos, accionFactura }
}
